import React, { useEffect } from 'react';
import CompanySimulatorWithMine from './personal/CompanySimulatorWithMine';
import PortfolioSimulatorSamplePreview from '../components/PortfolioSimulatorSamplePreview.js';
import BetaEndedCard from '../components/BetaEndedCard.js';
import { IconLock, IconRefresh } from '../components/Icons';
import PageHeading from '../components/PageHeading';
import { useDelayedTrueWhile } from '../hooks/useDelayedTrueWhile';
import { trackEvent } from '../utils/analytics';
import { membershipDeniedFallbackMessage } from '../utils/userFacingStrategyErrors';
import { getEntityDisplayName } from '../utils/activeEntity';

const TIER_WAIT_UI_DELAY_MS = 750;

function PortfolioSimulatorPage({
  user,
  isPro,
  tierReady = true,
  hasVectorAccess = null,
  hasStrategyData = false,
  loadingSheets = false,
  membershipDeniedText = '',
  showBetaEndMessage = false,
  onUpgrade,
  onNavigate,
  authLoading = false,
}) {
  const showTierWaitUi = useDelayedTrueWhile(!tierReady, TIER_WAIT_UI_DELAY_MS);
  const signedIn = Boolean(user?.email && String(user.email).trim());

  useEffect(() => {
    trackEvent('portfolio_simulator_opened', {
      source: 'portfolio_simulator_page',
      has_user: Boolean(user?.email),
    });
  }, [user?.email]);

  if (!tierReady) {
    if (!showTierWaitUi) return null;
    return (
      <>
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div
          className="results-tier-placeholder"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading your account access…</p>
          </div>
        </div>
      </>
    );
  }

  if (authLoading && !signedIn) {
    return (
      <div className="feature-gate-stack">
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div
          className="results-tier-placeholder"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="feature-gate-stack">
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div className="upgrade-required-page">
          <div className="upgrade-required-content">
            <div className="pro-lock-icon">
              <IconLock size={48} />
            </div>
            <h2 className="pro-lock-title">Portfolio Simulator</h2>
            <p className="pro-lock-subtitle">Sign in to continue</p>
            <p className="pro-lock-description">
              Sign in to run portfolio-level risk analysis and compare strategy combinations.
            </p>
            <button
              type="button"
              className="pro-lock-upgrade-button"
              onClick={() => onNavigate && onNavigate('login')}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadingSheets || hasVectorAccess === null) {
    return (
      <>
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div
          className="results-tier-placeholder"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="results-tier-placeholder-inner">
            <span className="results-tier-placeholder-spin" aria-hidden="true">
              <IconRefresh size={24} color="rgba(129, 140, 248, 0.9)" />
            </span>
            <p className="results-tier-placeholder-text">Loading your account access…</p>
          </div>
        </div>
      </>
    );
  }

  // A real refusal from the API — the user is not a member of this brand.
  if (!hasVectorAccess) {
    return (
      <>
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div className="results-spreadsheet-error" role="alert">
          {membershipDeniedText || membershipDeniedFallbackMessage()}
        </div>
      </>
    );
  }

  // Access is fine, the brand simply has nothing to simulate yet. This is NOT a
  // permission problem and must not read like one — it is the normal state of a
  // brand before its first strategy is uploaded.
  if (!hasStrategyData) {
    return (
      <>
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div className="results-tier-placeholder" role="status">
          <div className="results-tier-placeholder-inner">
            <p className="results-tier-placeholder-text">
              No strategies yet for {getEntityDisplayName()}. Add one on the Strategies page and upload its
              data in Strategy Data — it will appear here automatically.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!isPro) {
    if (showBetaEndMessage) {
      return (
        <>
          <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
          <div style={{ position: 'relative', minHeight: '400px' }}>
            <PortfolioSimulatorSamplePreview />
            <BetaEndedCard variant="overlay" />
          </div>
        </>
      );
    }
    // Render the sample preview underneath the lock card so the user can see
    // a real-shape rendering of the simulator (table, controls, ranks) behind
    // the gate. Matches the Bulk Risk / CSV Upload "preview behind overlay"
    // pattern. The preview's controls are already non-interactive (disabled
    // inputs), and the overlay's pointer-events: all blocks the rest.
    return (
      <>
        <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
        <div className="portfolio-sim-locked-wrapper">
          <PortfolioSimulatorSamplePreview />
          <div className="pro-lock-overlay">
            <div className="pro-lock-content">
              <div className="pro-lock-icon">
                <IconLock size={48} />
              </div>
              <h3 className="pro-lock-title">Portfolio Simulator</h3>
              <p className="pro-lock-subtitle">This feature is available in RiskLo Pro</p>
              <p className="pro-lock-description">
                Upgrade to instantly compare strategy combinations and run portfolio-level risk analysis.
              </p>
              <button
                type="button"
                className="pro-lock-upgrade-button"
                onClick={() => {
                  if (onUpgrade) {
                    onUpgrade('Upgrade to RiskLo Pro to access Portfolio Simulator');
                  }
                }}
              >
                Upgrade to RiskLo Pro
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Portfolio Simulator" description="Model combined strategies and projected outcomes." />
      <CompanySimulatorWithMine user={user} onUpgrade={onUpgrade} />
    </>
  );
}

export default PortfolioSimulatorPage;
